import {
  CommandBarItem,
  compactCommands,
  createCommand,
} from '@sketch/modules-common'
import { NotificationInfoFragment } from '@sketch/gql-types'

import { ReactComponent as CheckmarkIcon } from '@sketch/icons/checkmark-circle-17'
import { ReactComponent as EnvelopeIcon } from '@sketch/icons/mail-envelope-16'
import { ReactComponent as PinIcon } from '@sketch/icons/pushpin-17'
import { ReactComponent as OpenIcon } from '@sketch/icons/arrow-top-right-diagonal-16'
import { ReactComponent as WorkspaceIcon } from '@sketch/icons/workspace-17'
import { ReactComponent as FollowIcon } from '@sketch/icons/misc-notification-17'
import { ReactComponent as UnfollowIcon } from '@sketch/icons/misc-notification-slash-17'
import { ReactComponent as DownloadIcon } from '@sketch/icons/arrow-down-to-line-17'
import { ReactComponent as CloseIcon } from '@sketch/icons/cross-17'

export interface CreateNotificationCommandsProps {
  notification: NotificationInfoFragment
  /** In-app destination for document-bearing notifications (opens in a new tab). */
  openDocumentPath: string | null
  /** Workspace to navigate to, for workspace-invite notifications. */
  goToWorkspaceId: string | null
  /** In-app destination for a not-yet-accepted workspace invite. */
  openInvitePath: string | null
  /** Comment/mention thread the user can follow or unfollow. */
  canToggleSubscription: boolean
  isSubscribed: boolean
  /** Pending share invitation that can be accepted inline. */
  canAccept: boolean
  /** Downloadable asset URL, when the notification carries one. */
  assetPath: string | null
  onToggleReadState: () => void
  onTogglePin: () => void
  onOpenDocument: () => void
  onGoToWorkspace: () => void
  onOpenInvite: () => void
  onToggleSubscription: () => void
  onAccept: () => void
  onDownload: () => void
  onClose: () => void
}

/**
 * Commands for the currently-open notification (the "Notification" section).
 * Mirrors the actions in `InboxPreviewActions`/`MoreActionsMenu` so anything
 * reachable by mouse in the preview is also reachable from the command bar.
 */
export const createNotificationCommands = ({
  notification,
  openDocumentPath,
  goToWorkspaceId,
  openInvitePath,
  canToggleSubscription,
  isSubscribed,
  canAccept,
  assetPath,
  onToggleReadState,
  onTogglePin,
  onOpenDocument,
  onGoToWorkspace,
  onOpenInvite,
  onToggleSubscription,
  onAccept,
  onDownload,
  onClose,
}: CreateNotificationCommandsProps): CommandBarItem[] => {
  const isUnread = notification.state === 'UNREAD'
  const { isPinned } = notification

  return compactCommands([
    createCommand(
      {
        id: 'notification.toggle-read',
        analyticsId: isUnread
          ? 'notification.mark-as-read'
          : 'notification.mark-as-unread',
        title: isUnread ? 'Mark as Read' : 'Mark as Unread',
        section: 'Notification',
        keywords: ['mark', 'read', 'unread', 'seen'],
        icon: isUnread ? CheckmarkIcon : EnvelopeIcon,
        priority: 20,
      },
      onToggleReadState
    ),
    createCommand(
      {
        id: 'notification.toggle-pin',
        analyticsId: isPinned ? 'notification.unpin' : 'notification.pin',
        title: isPinned ? 'Unpin Notification' : 'Pin Notification',
        section: 'Notification',
        keywords: ['pin', 'unpin', 'pinned'],
        icon: PinIcon,
        priority: 18,
      },
      onTogglePin
    ),
    canAccept &&
      createCommand(
        {
          id: 'notification.accept-invitation',
          analyticsId: 'notification.accept-invitation',
          title: 'Accept Invitation',
          section: 'Notification',
          keywords: ['accept', 'invite', 'invitation', 'access'],
          icon: CheckmarkIcon,
          priority: 16,
        },
        onAccept
      ),
    !!assetPath &&
      createCommand(
        {
          id: 'notification.download-asset',
          analyticsId: 'notification.download-asset',
          title: 'Download Asset',
          section: 'Notification',
          keywords: ['download', 'asset', 'export', 'save'],
          icon: DownloadIcon,
          priority: 16,
        },
        onDownload
      ),
    !!openDocumentPath &&
      createCommand(
        {
          id: 'notification.open-document',
          analyticsId: 'notification.open-document',
          title: 'Open Document',
          section: 'Notification',
          keywords: ['open', 'document', 'go', 'see'],
          icon: OpenIcon,
          priority: 14,
        },
        onOpenDocument
      ),
    !!openInvitePath &&
      createCommand(
        {
          id: 'notification.open-invite',
          analyticsId: 'notification.open-invite',
          title: 'Open Invite',
          section: 'Notification',
          keywords: ['open', 'invite', 'invitation', 'workspace'],
          icon: OpenIcon,
          priority: 14,
        },
        onOpenInvite
      ),
    !!goToWorkspaceId &&
      createCommand(
        {
          id: 'notification.go-to-workspace',
          analyticsId: 'notification.go-to-workspace',
          title: 'Go to Workspace',
          section: 'Notification',
          keywords: ['go', 'workspace', 'open'],
          icon: WorkspaceIcon,
          priority: 14,
        },
        onGoToWorkspace
      ),
    canToggleSubscription &&
      createCommand(
        {
          id: 'notification.toggle-subscription',
          analyticsId: isSubscribed
            ? 'notification.unfollow-thread'
            : 'notification.follow-thread',
          title: isSubscribed ? 'Unfollow Thread' : 'Follow Thread',
          section: 'Notification',
          keywords: ['follow', 'unfollow', 'subscribe', 'thread', 'comments'],
          icon: isSubscribed ? UnfollowIcon : FollowIcon,
          priority: 12,
        },
        onToggleSubscription
      ),
    createCommand(
      {
        id: 'notification.close',
        analyticsId: 'notification.close',
        title: 'Close Notification',
        section: 'Notification',
        keywords: ['close', 'dismiss', 'back', 'preview'],
        icon: CloseIcon,
        priority: 6,
      },
      onClose
    ),
  ])
}
